import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";

const LibraryDocumentViewerContext = createContext(null);

export const useLibraryDocumentViewer = () => {
  const ctx = useContext(LibraryDocumentViewerContext);
  if (!ctx) {
    throw new Error("useLibraryDocumentViewer must be used within a LibraryDocumentViewerProvider");
  }
  return ctx;
};

export const LibraryDocumentViewerProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [document, setDocument] = useState(null);

  // Parent's refresh callback + whether any edit happened this session.
  const onUpdatedRef = useRef(null);
  const dirtyRef = useRef(false);
  const lastUpdateRef = useRef(null);

  const openDocument = useCallback((doc, { onUpdated } = {}) => {
    onUpdatedRef.current = onUpdated || null;
    dirtyRef.current = false;
    lastUpdateRef.current = null;
    setDocument(doc);
    setIsOpen(true);
  }, []);

  const closeDocument = useCallback(() => {
    // Fire the parent refetch ONCE, on close, only if something changed.
    if (dirtyRef.current && onUpdatedRef.current) {
      onUpdatedRef.current(lastUpdateRef.current);
    }
    onUpdatedRef.current = null;
    dirtyRef.current = false;
    lastUpdateRef.current = null;
    setIsOpen(false);
    setDocument(null);
  }, []);

  // Updates the in-modal document immediately. Does NOT call the parent —
  // that's deferred to closeDocument. Accepts either a bare tags array
  // (from TagSearch) or a partial-document object (from DetailsPanel).
  const notifyUpdated = useCallback((update) => {
    const patch = Array.isArray(update) ? { tags: update } : update || {};
    dirtyRef.current = true;
    lastUpdateRef.current = { ...(lastUpdateRef.current || {}), ...patch };
    setDocument((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  const value = useMemo(
    () => ({ isOpen, document, openDocument, closeDocument, notifyUpdated }),
    [isOpen, document, openDocument, closeDocument, notifyUpdated]
  );

  return (
    <LibraryDocumentViewerContext.Provider value={value}>
      {children}
    </LibraryDocumentViewerContext.Provider>
  );
};
